import { useState, useEffect, useRef } from 'react';
import { Bell, Check, X } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotificationsDropdown() {
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth0();
  const localUser = JSON.parse(localStorage.getItem('ct_user') || 'null');
  const currentUser = user || localUser;
  const loggedIn = isAuthenticated || !!localUser;

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!loggedIn) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [loggedIn]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchNotifications = async () => {
    try {
      const email = currentUser?.email || '';
      const { data } = await axios.get(`${API}/api/notifications`, { params: { user_email: email, limit: 10 } });
      setNotifications(data.notifications || []);
    } catch {}
  };

  const markRead = async (id) => {
    await axios.put(`${API}/api/notifications/${id}/read`).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!loggedIn) return null;

  const typeColors = { info: 'bg-blue-100 text-blue-600', warning: 'bg-yellow-100 text-yellow-700', success: 'bg-green-100 text-green-600', error: 'bg-red-100 text-red-600' };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notifications-bell"
        className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:text-navy dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-terracotta text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white dark:bg-gray-800 shadow-xl rounded-xl w-80 z-50 border border-gray-100 dark:border-gray-700 animate-fade-in overflow-hidden" data-testid="notifications-dropdown">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h4 className="font-serif font-bold text-navy dark:text-white text-sm">Notifications</h4>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No notifications yet</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${!n.read ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${typeColors[n.type] || typeColors.info}`}>
                      {n.type || 'info'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy dark:text-white truncate">{n.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    {!n.read && (
                      <button onClick={() => markRead(n.id)} className="text-gray-400 hover:text-green-500 flex-shrink-0" title="Mark as read">
                        <Check size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
